import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2, Sparkles } from 'lucide-react';
import api, { API_ENDPOINTS } from '../config/api';

const PostQualityScore = ({
  postType,
  title,
  description,
  category,
  subCategory,
  type,
  requirements = [],
  deliverables = [],
  skills = [],
  includes = [],
  onScoreChange,
}) => {
  const minScore = postType === 'gig' ? 80 : 60;
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const requestIdRef = useRef(0);
  const onScoreChangeRef = useRef(onScoreChange);

  useEffect(() => {
    onScoreChangeRef.current = onScoreChange;
  }, [onScoreChange]);

  const payloadKey = useMemo(
    () =>
      JSON.stringify({
        postType,
        title: title || '',
        description: description || '',
        category: category || '',
        subCategory: subCategory || '',
        type: type || '',
        requirements: (requirements || []).filter(Boolean),
        deliverables: (deliverables || []).filter(Boolean),
        skills: (skills || []).filter(Boolean),
        includes: (includes || []).filter(Boolean),
      }),
    [postType, title, description, category, subCategory, type, requirements, deliverables, skills, includes]
  );

  useEffect(() => {
    const parsed = JSON.parse(payloadKey);
    const plain = (parsed.description || '').replace(/<[^>]*>/g, ' ').trim();
    if (!parsed.title?.trim() && !plain) {
      const empty = {
        score: 0,
        minScore,
        passes: false,
        summary: 'Start filling title and description to see your quality score.',
        suggestions: [],
        missingInfo: [],
        strengths: [],
      };
      setResult(empty);
      setError('');
      setLoading(false);
      onScoreChangeRef.current?.(empty);
      return undefined;
    }

    const timer = setTimeout(async () => {
      const requestId = ++requestIdRef.current;
      setLoading(true);
      setError('');
      try {
        const { data } = await api.post(API_ENDPOINTS.AI.SCORE_POST, parsed);
        if (requestId !== requestIdRef.current) return;
        setResult(data);
        onScoreChangeRef.current?.(data);
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        const message = err.response?.data?.error || 'Unable to score right now';
        setError(message);
        const failSafe = {
          score: 0,
          minScore,
          passes: false,
          summary: message,
          suggestions: [],
          missingInfo: [],
          strengths: [],
        };
        setResult(failSafe);
        onScoreChangeRef.current?.(failSafe);
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    }, 900);

    return () => clearTimeout(timer);
  }, [payloadKey, minScore]);

  const score = result?.score ?? 0;
  const passes = score >= minScore;
  const scoreColor = passes
    ? 'text-green-600 dark:text-green-400'
    : 'text-red-600 dark:text-red-400';
  const barColor = passes ? 'bg-green-500' : 'bg-red-500';

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-accent" />
          Quality Score
        </h3>
        {loading && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
      </div>

      <div className="flex items-end gap-2 mb-2">
        <span className={`text-4xl font-bold tabular-nums ${scoreColor}`}>{score}</span>
        <span className="text-gray-500 dark:text-gray-400 mb-1">/ 100</span>
      </div>

      <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mb-3">
        <div
          className={`h-full transition-all duration-500 ${barColor}`}
          style={{ width: `${Math.min(100, Math.max(0, score))}%` }}
        />
      </div>

      {!passes && (
        <p className="text-sm font-medium text-red-600 dark:text-red-400 mb-3">
          Score must be above {minScore}% to post this {postType}.
        </p>
      )}

      {passes && (
        <p className="text-sm font-medium text-green-600 dark:text-green-400 mb-3 flex items-center gap-1.5">
          <CheckCircle2 className="w-4 h-4" />
          Ready to post — quality threshold met.
        </p>
      )}

      {result?.summary && (
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{result.summary}</p>
      )}

      {error && (
        <p className="text-sm text-amber-600 dark:text-amber-400 mb-3 flex items-start gap-1.5">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          {error}
        </p>
      )}

      {result?.missingInfo?.length > 0 && (
        <div className="mb-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">
            Include for clarity
          </p>
          <ul className="space-y-1">
            {result.missingInfo.map((item) => (
              <li key={item} className="text-sm text-gray-700 dark:text-gray-300 flex gap-2">
                <span className="text-accent">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {result?.suggestions?.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">
            Suggestions
          </p>
          <ul className="space-y-1">
            {result.suggestions.map((item) => (
              <li key={item} className="text-sm text-gray-700 dark:text-gray-300 flex gap-2">
                <span className="text-accent">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default PostQualityScore;
